import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Crown, Sparkles, Palette, Camera, Heart, Sun, Moon, 
  User, Shirt, Sofa, Waves, Building, Flame, Snowflake,
  ChevronRight, Download, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Builder options
const MODELS = [
  { id: 'blonde', name: 'Blonde Bombshell', preview: '👱‍♀️' },
  { id: 'brunette', name: 'Brunette Vixen', preview: '👩' },
  { id: 'redhead', name: 'Redhead Temptress', preview: '👩‍🦰' },
  { id: 'platinum', name: 'Platinum Goddess', preview: '👩‍🦳' },
];

const POSES = [
  { id: 'lounge', name: 'Seductive Lounge', icon: Sofa },
  { id: 'mirror', name: 'Mirror Tease', icon: User },
  { id: 'bed', name: 'Bed Arch', icon: Heart },
  { id: 'shower', name: 'Shower Silhouette', icon: Waves },
  { id: 'window', name: 'Window Light', icon: Sun },
  { id: 'standing', name: 'Standing Pose', icon: User },
];

const OUTFITS = [
  { id: 'lace_teddy', name: 'Lace Teddy', color: '#E91E63' },
  { id: 'sheer_bodysuit', name: 'Sheer Bodysuit', color: '#9C27B0' },
  { id: 'garter_set', name: 'Garter Set', color: '#F44336' },
  { id: 'silk_robe', name: 'Silk Robe', color: '#D4AF37' },
  { id: 'leather', name: 'Leather Look', color: '#212121' },
  { id: 'white_lace', name: 'White Lace', color: '#FAFAFA' },
];

const SETTINGS = [
  { id: 'bedroom', name: 'Luxury Bedroom', icon: Heart },
  { id: 'beach', name: 'Beach Sunset', icon: Sun },
  { id: 'hotel', name: 'Luxury Hotel', icon: Building },
  { id: 'office', name: 'Forbidden Office', icon: Building },
  { id: 'pool', name: 'Private Pool', icon: Waves },
  { id: 'studio', name: 'Photo Studio', icon: Camera },
];

const PRICING = {
  basic_build: { name: 'Basic Build', tokens: 15, images: 3, video: false },
  premium_craft: { name: 'Premium Craft', tokens: 30, images: 8, video: true },
  vip_masterpiece: { name: 'VIP Masterpiece', tokens: 50, images: 15, video: true, extras: true },
};

export default function PromptBuilder() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState({
    model: null,
    pose: null,
    outfit: null,
    mood: 50,
    setting: null,
    personalMessage: '',
  });
  const [selectedTier, setSelectedTier] = useState('premium_craft');
  const [generating, setGenerating] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  // Fetch credit balance
  useState(() => {
    if (user) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setCreditBalance(res.data?.balance || 0))
        .catch(() => {});
    }
  }, [user]);

  const handleSelect = (key, value) => {
    setSelections(prev => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return selections.model;
      case 2: return selections.pose;
      case 3: return selections.outfit;
      case 4: return true; // Mood always has value
      case 5: return selections.setting;
      default: return true;
    }
  };

  const [generatedImage, setGeneratedImage] = useState(null);

  const handleGenerate = async () => {
    if (!user) {
      toast.error(t('builder.loginRequired') || 'Je moet ingelogd zijn');
      navigate('/login');
      return;
    }
    
    const tier = PRICING[selectedTier];
    
    // Check if user has enough tokens
    if (creditBalance < tier.tokens) {
      toast.error(`Niet genoeg tokens! Je hebt ${creditBalance}, nodig: ${tier.tokens}`);
      navigate('/credits');
      return;
    }

    // Check all selections made
    if (!selections.model || !selections.pose || !selections.outfit || !selections.setting) {
      toast.error('Maak eerst alle selecties compleet!');
      return;
    }
    
    setGenerating(true);
    toast.info('🎨 Je kunstwerk wordt gemaakt... Dit kan tot 60 seconden duren.');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/builder/generate`, {
        model_type: selections.model,
        pose: selections.pose,
        outfit: selections.outfit,
        setting: selections.setting,
        mood: selections.mood,
        tier: selectedTier,
        personal_message: selections.personalMessage || ''
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for AI generation
      });
      
      if (response.data.success) {
        setCreditBalance(response.data.new_balance);
        setGeneratedImage(response.data.image_url);
        toast.success(`🎨 Je kunstwerk is klaar! ${response.data.tokens_spent} tokens gebruikt.`);
      }
    } catch (error) {
      console.error('Generation error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Generatie duurde te lang. Probeer het opnieuw.');
      } else {
        toast.error(error.response?.data?.detail || 'Er ging iets mis bij het genereren');
      }
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `ella-gold-art-${Date.now()}.png`;
    link.click();
  };

  const getMoodLabel = (value) => {
    if (value < 25) return 'Playful Flirt';
    if (value < 50) return 'Soft Sensual';
    if (value < 75) return 'Intense Desire';
    return 'Raw Passion';
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">{t('builder.badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4">
            {t('builder.title')} <span className="brand-gold text-5xl sm:text-6xl lg:text-7xl">{t('builder.titleAccent')}</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {t('builder.subtitle')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-12 overflow-x-auto pb-4">
            {[
              { num: 1, label: t('builder.steps.model'), icon: User },
              { num: 2, label: t('builder.steps.pose'), icon: Camera },
              { num: 3, label: t('builder.steps.outfit'), icon: Shirt },
              { num: 4, label: t('builder.steps.mood'), icon: Heart },
              { num: 5, label: t('builder.steps.setting'), icon: Sun },
              { num: 6, label: t('builder.steps.generate'), icon: Sparkles },
            ].map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.num;
              const isComplete = step > s.num;
              return (
                <div key={s.num} className="flex items-center">
                  <button
                    onClick={() => setStep(s.num)}
                    className={`flex flex-col items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                        : isComplete 
                          ? 'text-[#D4AF37]' 
                          : 'text-gray-500'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive 
                        ? 'bg-[#D4AF37] text-black' 
                        : isComplete 
                          ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                          : 'bg-white/5'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs whitespace-nowrap">{s.label}</span>
                  </button>
                  {i < 5 && (
                    <div className={`w-8 h-0.5 ${step > s.num ? 'bg-[#D4AF37]' : 'bg-white/10'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          <div className="glass rounded-2xl p-8 mb-8 min-h-[400px]">
            {/* Step 1: Model Selection */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-medium mb-6">Choose Your Model</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {MODELS.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => handleSelect('model', model.id)}
                      className={`p-6 rounded-xl border transition-all ${
                        selections.model === model.id
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                      data-testid={`model-${model.id}`}
                    >
                      <div className="text-5xl mb-3">{model.preview}</div>
                      <p className="font-medium">{model.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Pose Selection */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-medium mb-6">Select Pose</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {POSES.map((pose) => {
                    const Icon = pose.icon;
                    return (
                      <button
                        key={pose.id}
                        onClick={() => handleSelect('pose', pose.id)}
                        className={`p-6 rounded-xl border transition-all ${
                          selections.pose === pose.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                        data-testid={`pose-${pose.id}`}
                      >
                        <Icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                        <p className="font-medium">{pose.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Outfit Selection */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-medium mb-6">Pick Outfit</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {OUTFITS.map((outfit) => (
                    <button
                      key={outfit.id}
                      onClick={() => handleSelect('outfit', outfit.id)}
                      className={`p-6 rounded-xl border transition-all ${
                        selections.outfit === outfit.id
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                      data-testid={`outfit-${outfit.id}`}
                    >
                      <div 
                        className="w-12 h-12 rounded-full mx-auto mb-3 border-2 border-white/20"
                        style={{ backgroundColor: outfit.color }}
                      />
                      <p className="font-medium">{outfit.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Mood Slider */}
            {step === 4 && (
              <div>
                <h2 className="text-2xl font-medium mb-6">Set the Mood</h2>
                <div className="max-w-md mx-auto py-12">
                  <div className="flex justify-between mb-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1"><Snowflake className="w-4 h-4" /> Playful</span>
                    <span className="flex items-center gap-1"><Flame className="w-4 h-4" /> Intense</span>
                  </div>
                  <Slider
                    value={[selections.mood]}
                    onValueChange={(value) => handleSelect('mood', value[0])}
                    max={100}
                    step={1}
                    className="mb-6"
                  />
                  <div className="text-center">
                    <span className="text-2xl font-medium gold-text">{getMoodLabel(selections.mood)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Setting Selection */}
            {step === 5 && (
              <div>
                <h2 className="text-2xl font-medium mb-6">Choose Setting</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {SETTINGS.map((setting) => {
                    const Icon = setting.icon;
                    return (
                      <button
                        key={setting.id}
                        onClick={() => handleSelect('setting', setting.id)}
                        className={`p-6 rounded-xl border transition-all ${
                          selections.setting === setting.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                        data-testid={`setting-${setting.id}`}
                      >
                        <Icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                        <p className="font-medium">{setting.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 6: Generate & Payment */}
            {step === 6 && (
              <div>
                <h2 className="text-2xl font-medium mb-6 text-center">Your Fantasy is Ready</h2>
                
                {/* Summary */}
                <div className="p-6 bg-white/5 rounded-xl mb-8 max-w-md mx-auto">
                  <h3 className="text-lg font-medium mb-4 gold-text">Your Selection</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Model:</span> {MODELS.find(m => m.id === selections.model)?.name || '-'}</p>
                    <p><span className="text-gray-400">Pose:</span> {POSES.find(p => p.id === selections.pose)?.name || '-'}</p>
                    <p><span className="text-gray-400">Outfit:</span> {OUTFITS.find(o => o.id === selections.outfit)?.name || '-'}</p>
                    <p><span className="text-gray-400">Mood:</span> {getMoodLabel(selections.mood)}</p>
                    <p><span className="text-gray-400">Setting:</span> {SETTINGS.find(s => s.id === selections.setting)?.name || '-'}</p>
                  </div>
                </div>

                {/* Tier Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {Object.entries(PRICING).map(([id, tier]) => (
                    <button
                      key={id}
                      onClick={() => setSelectedTier(id)}
                      className={`p-6 rounded-xl border text-left transition-all ${
                        selectedTier === id
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-white/10 bg-white/5'
                      }`}
                      data-testid={`tier-${id}`}
                    >
                      <h4 className="font-medium mb-2">{tier.name}</h4>
                      <p className="text-2xl font-bold gold-text mb-2">{tier.tokens} tokens</p>
                      <p className="text-sm text-gray-400">
                        {tier.images} images {tier.video && '+ video'}
                        {tier.extras && ' + voiceover'}
                      </p>
                    </button>
                  ))}
                </div>

                {/* Generate Button */}
                <div className="text-center">
                  <Button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="btn-gold px-12 py-6 text-lg"
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        {t('builder.generating')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        {t('builder.generate')} - {PRICING[selectedTier].tokens} tokens
                      </span>
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 mt-4">Your balance: {creditBalance} tokens</p>
                </div>

                {/* Generated Image Result */}
                {generatedImage && (
                  <div className="mt-12 text-center">
                    <div className="inline-block p-2 bg-gradient-to-r from-[#D4AF37]/20 via-[#FFD700]/20 to-[#D4AF37]/20 rounded-2xl">
                      <div className="bg-black rounded-xl overflow-hidden">
                        <img 
                          src={generatedImage} 
                          alt="Your Generated Art"
                          className="max-w-full max-h-[600px] mx-auto"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-center gap-4">
                      <Button onClick={downloadImage} className="btn-gold">
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setGeneratedImage(null);
                          setStep(1);
                          setSelections({
                            model: null,
                            pose: null,
                            outfit: null,
                            mood: 50,
                            setting: null,
                            personalMessage: ''
                          });
                        }}
                        className="border-white/20"
                      >
                        Create Another
                      </Button>
                    </div>
                    <p className="text-gray-400 text-sm mt-4">
                      ✨ Your exclusive art has been saved to your dashboard
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Back
            </Button>
            {step < 6 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="btn-gold"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
